import { type CustomTooltipProps } from "ag-grid-react"
import { formatNumber, formatDecimalToPercent } from "~/utils/formatting"
import HeaderTooltip from "./HeaderTooltip"

export type CollectionComparisonTooltipParams = {
  originalValue: number
  comparisonValue: number
  originalName: string
  comparisonName: string
  metricLabel: string
  hidePercentage?: boolean
}

const CollectionComparisonTooltip = (props: CustomTooltipProps) => {
  // Show header tooltip for header hover
  if (props.location === "header") {
    return <HeaderTooltip {...props} />
  }

  // Only show cell tooltip when on a cell
  if (props.location !== "cell") {
    return null
  }

  const data = props.value as CollectionComparisonTooltipParams | undefined

  if (!data) {
    return null
  }

  const {
    originalValue,
    comparisonValue,
    originalName,
    comparisonName,
    metricLabel,
    hidePercentage,
  } = data

  const percentageDiff =
    !hidePercentage && comparisonValue !== 0
      ? (originalValue - comparisonValue) / comparisonValue
      : null

  return (
    <div className="bg-base border border-base rounded-lg shadow-lg p-3 min-w-[200px]">
      <div className="text-sm font-medium text-fg-base mb-2">{metricLabel} Comparison</div>
      <div className="flex flex-col gap-1.5">
        <div className="flex justify-between items-center">
          <span className="text-fg-subtle text-sm">{originalName}:</span>
          <span className="text-fg-base font-medium">{formatNumber(originalValue, 0)}</span>
        </div>
        <div className="flex justify-between items-center">
          <span className="text-fg-subtle text-sm">{comparisonName}:</span>
          <span className="text-fg-base font-medium">{formatNumber(comparisonValue, 0)}</span>
        </div>
        {percentageDiff !== null && (
          <>
            <div className="border-t border-base my-1" />
            <div className="flex justify-between items-center">
              <span className="text-fg-subtle text-sm">Difference:</span>
              <span
                className={`font-medium ${percentageDiff > 0 ? "text-green-600" : percentageDiff < 0 ? "text-red-600" : "text-fg-base"}`}
              >
                {percentageDiff > 0 ? "+" : ""}
                {formatDecimalToPercent(percentageDiff, 1)}
              </span>
            </div>
          </>
        )}
        {comparisonValue === 0 && originalValue > 0 && (
          <>
            <div className="border-t border-base my-1" />
            <div className="text-fg-subtle text-xs">No comparison data available for this item</div>
          </>
        )}
      </div>
    </div>
  )
}

export default CollectionComparisonTooltip
